import type { LabKey } from './types';

export const LAB_ORDER: LabKey[] = [
  'hto',
  'hb',
  'gb',
  'plaq',
  'u',
  'cr',
  'iono',
  'ca',
  'cai',
  'p',
  'mg',
  'eabv',
  'eaba',
  'eab',
  'hep',
  'ggt',
  'coag',
  'pcr',
  'vsg',
  'ldh',
  'alb',
  'prot',
  'tacrol',
  'ac_urico',
  'ferritina',
  'probnp',
  'tsh',
  't4l',
  't4',
  't3',
  'glu',
  'lactato',
  'cpk',
  'troponina',
  'ckmb',
  'dd',
  'amilasa',
  'lipasa',
];

export const LAB_LABELS: Record<LabKey, string> = {
  hto: 'Hto',
  hb: 'Hb',
  gb: 'GB',
  plaq: 'Plaq',
  u: 'U',
  cr: 'Cr',
  iono: 'Iono',
  ca: 'Ca',
  cai: 'CaI',
  p: 'P',
  mg: 'Mg',
  eabv: 'EABv',
  eaba: 'EABa',
  eab: 'EAB',
  hep: 'Hep',
  ggt: 'GGT',
  coag: 'Coag',
  pcr: 'PCR',
  vsg: 'VSG',
  ldh: 'LDH',
  alb: 'Alb',
  prot: 'PrT',
  tacrol: 'Tacrol',
  ac_urico: 'Ac úrico',
  ferritina: 'Ferritina',
  probnp: 'ProBNP',
  tsh: 'TSH',
  t4l: 'T4L',
  t4: 'T4',
  t3: 'T3',
  glu: 'Glu',
  lactato: 'AL',
  cpk: 'CPK',
  troponina: 'TropT',
  ckmb: 'CKMB',
  dd: 'DD',
  amilasa: 'Amilasa',
  lipasa: 'Lipasa',
};

export const LAB_ALIASES: Array<{ key: LabKey; pattern: string }> = [
  { key: 'ac_urico', pattern: 'Ac(?:ido|ícido)?\\s*[UÚ]rico|Ac\\s*U' },
  { key: 'troponina', pattern: 'Trop(?:onina)?\\s*T(?:UltraS)?|TnT' },
  { key: 'probnp', pattern: 'T-?PROBNP|ProBNP' },
  { key: 'ferritina', pattern: 'Ferritina|Ferrit' },
  { key: 'amilasa', pattern: 'Amilasa|Amil' },
  { key: 'lipasa', pattern: 'Lipasa|Lp' },
  { key: 'iono', pattern: 'Ionograma|Iono' },
  { key: 'coag', pattern: 'Coagulograma|Coagulo|Coag' },
  { key: 'eabv', pattern: 'EABv' },
  { key: 'eaba', pattern: 'EABa' },
  { key: 'eab', pattern: 'EAB' },
  { key: 'hep', pattern: 'Hepatograma|Hepato|Hep' },
  { key: 'plaq', pattern: 'Plaquetas(?:\\s+recuento)?|Recuento\\s+de\\s+plaquetas|Plaq|Plq' },
  { key: 'hto', pattern: 'Hematocrito|Hto' },
  { key: 'hb', pattern: 'Hemoglobina|Hgb|Hb|Hg' },
  { key: 'gb', pattern: 'Recuento\\s+de\\s+gl[oó]bulos\\s+blancos|Leucocitos|GB|Gb' },
  { key: 'cr', pattern: 'Creatinina(?:\\s+en\\s+sangre)?|Cr' },
  { key: 'u', pattern: 'Urea(?:\\s+en\\s+sangre)?|Ur|U' },
  { key: 'cai', pattern: 'Calcio\\s+i[oó]nico|Ca\\s*I|CaI|Cai' },
  { key: 'ca', pattern: 'Calcio(?:\\s+en\\s+sangre)?|Ca\\s*S|CaS|Ca' },
  { key: 'mg', pattern: 'Magnesio(?:\\s+en\\s+sangre)?|Mg' },
  { key: 'pcr', pattern: 'PCR' },
  { key: 'vsg', pattern: 'VSG|ERS|ESD' },
  { key: 'ldh', pattern: 'LDH' },
  { key: 'alb', pattern: 'Albumina|Albúmina|Alb' },
  { key: 'prot', pattern: 'Prote[ií]nas?\\s*(?:totales?)?|PrT|Prot(?:\\s*tot)?' },
  { key: 'ggt', pattern: 'Gamma\\s+glutamil\\s+transferasa|GGT' },
  { key: 'tacrol', pattern: 'Tacrol(?:emia)?' },
  { key: 'tsh', pattern: 'TSH' },
  { key: 't4l', pattern: 'T4L' },
  { key: 't4', pattern: 'T4' },
  { key: 't3', pattern: 'T3' },
  { key: 'glu', pattern: 'Glucosa|Gluc|Glu' },
  { key: 'lactato', pattern: 'Ac(?:ido)?\\s*L(?:actico)?|Lactato|Lact|AL' },
  { key: 'cpk', pattern: 'CPK' },
  { key: 'ckmb', pattern: 'CKMB' },
  { key: 'dd', pattern: 'Dimero\\s*D|D[íi]mero\\s*D|DD' },
  { key: 'p', pattern: 'F[oó]sforo(?:\\s+en\\s+sangre)?|P' },
];

export const SECONDARY_KEYS = new Set<LabKey>(['alb', 'ca', 'cai', 'p', 'mg', 'ac_urico']);

export const SUPPLEMENTAL_ALIASES = [
  'Ecodop Hep',
  'TC Tx c/',
  'TC Tx',
  'TcTx',
  'TAC Tx',
  'TC SNC',
  'TcSNC',
  'TC Cer',
  'TC AP',
  'AngioTC',
  'RMN órbita',
  'RMN SNC',
  'RMN Cer',
  'RMN col',
  'RxTx',
  'Rx Tx',
  'Eco♥',
  'EcoRV',
  'EcoVC',
  'Eco abd',
  'ETE',
  'ECG',
  'EEG',
  'UC',
  'Uroc',
  'HC',
  'BAL',
  'Cult Qx',
  'FARES',
  'KPC',
  'CD',
  'LMF',
  'MF',
  'SU',
  'OC',
];

export const MICRO_TYPES = new Set(['UC', 'HC', 'BAL', 'CULT QX', 'FARES', 'KPC', 'CD', 'LMF', 'MF']);
